import { css } from '@emotion/react'
import { Button, Cell, FormControl, Grid, HFlow, isEmpty, Theme, useTheme } from 'bold-ui'
import {
  CheckboxField,
  DateField,
  ErrorField,
  HorarioAgendaSelectField,
  LocalAtendimentoSelectField,
  LotacaoAgendaSelectField,
  TextAreaField,
} from 'components/form'
import { addMonths, parseISO } from 'date-fns'
import { useHasAgendaFixadaQuery } from 'graphql/hooks.generated'
import { AcessoCbo } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment } from 'react'
import { useField } from 'react-final-form'
import { isDataAgendamentoInvalida } from 'util/atendimento/isDataAgendamentoInvalida'
import { MetaPath, MetaRoot } from 'util/metaPath'

import { TipoPreNatal } from '../../../pre-natal/model-preNatal'
import { AgendarConsultaSectionModel } from './model-agendarConsultaAtendimento'
import { getAtendimentoCellSizes, getRegistroTardioCellSizes } from './util-agendarConsulta'

export interface AgendarConsultaSectionProps {
  name: MetaRoot<AgendarConsultaSectionModel>
  errorPath: MetaPath<AgendarConsultaSectionModel>
  isProximaConsulta?: boolean
  hasPainelLateral?: boolean
  isRegistroTardio?: boolean
  tipoPreNatal?: TipoPreNatal
  setIsFormAgendarProximaConsultaOpen?(isFormOpen: boolean): void
  onSubmitFormAgendarProximaConsulta?(): void
  cbosAcesso?: AcessoCbo[]
  showHorarioFinal?: boolean
}

export function AgendarConsultaSection(props: Readonly<AgendarConsultaSectionProps>) {
  const {
    name,
    errorPath,
    isProximaConsulta,
    isRegistroTardio,
    hasPainelLateral = true,
    setIsFormAgendarProximaConsultaOpen,
    onSubmitFormAgendarProximaConsulta,
    cbosAcesso,
    showHorarioFinal = false,
  } = props

  const theme = useTheme()
  const styles = createStyles(theme, isProximaConsulta)

  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()

  const {
    input: { value: lotacao },
  } = useField(name.lotacao.absolutePath(), { subscription: { value: true } })

  const {
    input: { value: data },
  } = useField(name.data.absolutePath(), { subscription: { value: true } })

  const {
    input: { value: isForaUbs },
  } = useField(name.isForaUbs.absolutePath(), { subscription: { value: true } })

  const dataAgendamentoEhInvalida = isDataAgendamentoInvalida(data, serverTime)
  const lotacaoPreenchida = !isEmpty(lotacao)

  const handleClickCancelar = () => setIsFormAgendarProximaConsultaOpen(false)
  const handleClickSalvar = () => onSubmitFormAgendarProximaConsulta()
  const {
    data: { hasAgendaFixada },
    loading,
  } = useHasAgendaFixadaQuery()

  const cellSizes = isRegistroTardio
    ? getRegistroTardioCellSizes(hasPainelLateral, showHorarioFinal)
    : getAtendimentoCellSizes(hasPainelLateral, showHorarioFinal)

  return (
    <div css={styles.container}>
      <Grid>
        <Cell size={12}>
          <ErrorField name={errorPath} type='alert' ignoreObjectError />
        </Cell>
        <Cell md={cellSizes.lotacao.md} lg={cellSizes.lotacao.lg}>
          {!loading && (
            <LotacaoAgendaSelectField
              name={name.lotacao}
              label='Profissional'
              required={isProximaConsulta}
              isAgendaAd={false}
              somenteCadastrarAgendamento
              cbosAcesso={cbosAcesso}
              hasAgendaFixada={hasAgendaFixada}
            />
          )}
        </Cell>
        {showHorarioFinal && <Cell md={cellSizes.emptyCell.md} lg={cellSizes.emptyCell.lg} />}
        <Cell md={cellSizes.data.md} lg={cellSizes.data.lg}>
          <DateField
            name={name.data}
            label='Data do agendamento'
            required={lotacaoPreenchida}
            disabled={!lotacaoPreenchida}
            minDate={serverTime}
            maxDate={addMonths(serverTime, 6)}
          />
        </Cell>
        <Cell md={cellSizes.horarios.md} lg={cellSizes.horarios.lg}>
          <HorarioAgendaSelectField
            name={name.horario}
            lotacaoId={lotacao?.id}
            disabled={dataAgendamentoEhInvalida}
            required={!dataAgendamentoEhInvalida}
            labels={
              showHorarioFinal
                ? { inicial: 'Horário inicial', final: 'Horário final' }
                : { inicial: 'Horário do agendamento' }
            }
            showHorarioFinal={showHorarioFinal}
            dia={data ? parseISO(data) : undefined}
            clearable
          />
        </Cell>
        {isRegistroTardio && (
          <Fragment>
            <Cell size={2} alignSelf='center'>
              <FormControl label={<span>&nbsp;</span>}>
                <CheckboxField label='Fora da UBS' name={name.isForaUbs} />
              </FormControl>
            </Cell>
            {isForaUbs && (
              <Cell size={4}>
                <LocalAtendimentoSelectField
                  name={name.localAtendimento}
                  required={isForaUbs}
                  label='Local de atendimento'
                  excludeUbs
                />
              </Cell>
            )}
          </Fragment>
        )}
        <Cell size={isProximaConsulta ? 9 : 12}>
          <TextAreaField
            label='Observações'
            name={name.observacoes}
            resize='vertical'
            maxLength={200}
            style={styles.observacoesField}
          />
        </Cell>
        {isProximaConsulta && (
          <Cell size={3} alignSelf='center'>
            <HFlow justifyContent='flex-end' hSpacing={1}>
              <Button size='small' kind='normal' onClick={handleClickCancelar} style={styles.button}>
                Cancelar
              </Button>
              <Button size='small' kind='primary' onClick={handleClickSalvar} style={styles.button}>
                Salvar
              </Button>
            </HFlow>
          </Cell>
        )}
      </Grid>
    </div>
  )
}

const createStyles = (theme: Theme, isProximaConsulta: boolean) => ({
  container: css`
    padding: 1rem;
    ${isProximaConsulta ? `background-color: ${theme.pallete.primary.c90}` : ''};
  `,
  observacoesField: css`
    min-height: 4rem;
    width: 100%;
  `,
  button: css`
    min-width: 6rem;
  `,
})
